/**
 * Hook that polls the backend health endpoint to detect when the AI
 * service (Azure OpenAI) is unavailable.  Returns a boolean flag that
 * the TherapistPanel can use to render a persistent banner.
 *
 * Polling interval: 30 s when healthy, 10 s when degraded so the
 * banner auto-dismisses quickly once the service recovers.
 */

import { useEffect, useRef, useState } from 'react';
import { apiFetch, buildApiUrl } from '@/utils/apiClient';

const HEALTHY_INTERVAL_MS = 30_000;
const DEGRADED_INTERVAL_MS = 10_000;

export function useAIHealthStatus() {
  const [aiAvailable, setAiAvailable] = useState(true);
  const aiAvailableRef = useRef(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const res = await apiFetch(buildApiUrl('/health'));
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          // The health endpoint returns { ai_available: bool } when
          // the field is present.  Fall back to true so that a plain
          // 200 without the field doesn't trigger the banner.
          const available = data.ai_available !== false;
          aiAvailableRef.current = available;
          setAiAvailable(available);
        } else {
          // A liveness/auth response does not establish AI availability.
          // Keep the last known state until a successful probe resolves it.
        }
      } catch {
        // Network error → assume degraded
        if (!cancelled) {
          aiAvailableRef.current = false;
          setAiAvailable(false);
        }
      }

      if (!cancelled) {
        const delay = aiAvailableRef.current ? HEALTHY_INTERVAL_MS : DEGRADED_INTERVAL_MS;
        timerRef.current = setTimeout(check, delay);
      }
    };

    // Initial check after a short delay so we don't block first render
    timerRef.current = setTimeout(check, 2_000);

    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  return { aiAvailable };
}
